import React, { useCallback, useMemo, useRef, useState } from 'react';
import { createContext } from '@shared/utils/createContext';
import type { ShippingAddressFragment } from '@graphql/aliases';
import { useDisclosure } from '@withjoy/joykit';
import { AddressDialog, AddressReminderDialog } from '@apps/registry/common/components';
import { CurrencyDialog } from '@apps/registry/common/components/CurrencyDialog';
import type { DateFragment } from '@graphql/fragments/coreTypeDefs.graphql.generated';
import type { EventRegistryCurrencyFragment, RegistryCurrencyCodesFragment } from '@graphql/fragments/currency.graphql.generated';
import { useAddCustomGiftDialogContext } from '../AddCustomGiftDialog/AddCustomGiftDialog.provider';
import { useInstallBrowserButtonProps } from '@apps/registry/admin/hooks/useInstallBrowserButton';
import { ShopCurrencyWarningDialog } from '../Catalog/components/ShopCurrencyWarningDialog';
import { LOCALE } from '@shared/utils/currency/constants';
import { useBrowserLocale } from '@shared/utils/currency/useLocale';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import type { AdminRegistryList, RegistryOrderList, RegistryList } from '../../state/RegistryProducts';
import { ShippingAddressTelemetryProvider } from './ShippingAddress.telemetry';
import { useCookedProductList } from '../../selectors/ProductListSelector';
import { GIFT_COUNT_OFFSET } from '../../util/constants';

type FuncArgType = () => void;

export type GeneralDialogsDriverContext = {
  isAddressDialogOpen: boolean;
  openAddressDialog: (from: string) => void;
  closeAddressDialog: () => void;

  isAddressReminderDialogOpen: boolean;
  openAddressReminderDialog: () => void;
  closeAddressReminderDialog: () => void;

  isCurrencyDialogOpen: boolean;
  openCurrencyDialog: () => void;
  closeCurrencyDialog: () => void;
  registryShippingAddress: ShippingAddressFragment | null;

  showOutOfUSAWarningOrAddItem: (funcArg: FuncArgType) => void;

  /**
   * View this state chart to view the expected states
   * https://stately.ai/viz/416e6d06-9f0b-4c39-86d2-5d165275711c
   *
   * If no address and product type is not cash or charity:
   *  - Show address reminder dialog
   *    - if "add later", add catalog item
   *    - Open address dialog
   *
   * If address:
   *   - If domestic address, add catalog item
   *   - If international address, show currency warning dialog
   */
  verifyAddressAndTryAddCatalogItem: (funcArg: FuncArgType) => void;
};

const [Provider, useShippingAddressAndCurrencyDialogsContext] = createContext<GeneralDialogsDriverContext>({ name: 'GeneralDialogsProvider' });

interface GeneralDialogsDriverProviderProps
  extends Readonly<{
    eventId: string;
    registryShippingAddress: ShippingAddressFragment | null;
    registryCurrency: Maybe<EventRegistryCurrencyFragment>;
    onSetRegistryCurrency?: (payload: RegistryCurrencyCodesFragment) => void;
    finalizedEventDate: Maybe<DateFragment>;
    orders?: RegistryOrderList;
    registries?: AdminRegistryList | RegistryList | null;
    website?: string | null;
  }> {}

const useDeferredAddCatalogItemCallback = () => {
  // Opting to use ref so that we can consistently keep it up to date
  const deferredAddCatalogItemCallbackRef = useRef<FuncArgType | null>(null);

  const clearDeferredAddCatalogItemCb = useCallback(() => {
    deferredAddCatalogItemCallbackRef.current = null;
  }, []);

  const deferAddCatalogItemCb = useCallback((cb: FuncArgType) => {
    deferredAddCatalogItemCallbackRef.current = cb;
  }, []);

  const executeDeferredAddCatalogItemCb = useCallback(() => {
    deferredAddCatalogItemCallbackRef.current?.();
    clearDeferredAddCatalogItemCb();
  }, [clearDeferredAddCatalogItemCb]);

  const checkHasDeferredAddCatalogItemCb = useCallback(() => {
    return !!deferredAddCatalogItemCallbackRef.current;
  }, []);

  return {
    /**
     * Stable utility function to check if there is a callback currently queued.
     */
    checkHasDeferredAddCatalogItemCb,
    /**
     * Flush any stored callback.
     */
    clearDeferredAddCatalogItemCb,
    /**
     * Collect the callback for later usage.
     */
    deferAddCatalogItemCb,
    /**
     * Will execute any outstanding add catalog item callbacks, if any.
     */
    executeDeferredAddCatalogItemCb,
  };
};

/**
 * Must be a child of `AddCustomGiftDialogProvider`.
 *
 * Handles:
 * - CurrencyDialog
 * - AddressReminderDialog
 * - AddressDialog
 * - ShopCurrencyWarningDialog
 */

export const ShippingAddressAndCurrencyDialogsProvider: React.FC<GeneralDialogsDriverProviderProps> = props => {
  const { eventId, children, onSetRegistryCurrency, registryShippingAddress, registryCurrency, website, finalizedEventDate, registries, orders } = props;

  const { isOpen: isAddressDialogOpen, onOpen: openAddressDialog, onClose: closeAddressDialog } = useDisclosure();
  const { isOpen: isCurrencyDialogOpen, onOpen: openCurrencyDialog, onClose: closeCurrencyDialog } = useDisclosure();
  const { isOpen: isCurrencyWarningDialogOpen, onOpen: openCurrencyWarningDialog, onClose: closeCurrencyWarningDialog } = useDisclosure();
  const { isOpen: isAddressReminderDialogOpen, onOpen: openAddressReminderDialog, onClose: closeAddressReminderDialog } = useDisclosure();

  const { clearDeferredAddCatalogItemCb, deferAddCatalogItemCb, checkHasDeferredAddCatalogItemCb, executeDeferredAddCatalogItemCb } = useDeferredAddCatalogItemCallback();
  const { openModal: openAddCustomGiftDialog } = useAddCustomGiftDialogContext();
  const [addressDialogTrigger, setAddressDialogTrigger] = useState<string>('');
  const cookedProducts = useCookedProductList(registries || [], orders, 'everything');

  const handleOnAddCustomGiftClick = useCallback(() => {
    clearDeferredAddCatalogItemCb();
    openAddCustomGiftDialog();
  }, [clearDeferredAddCatalogItemCb, openAddCustomGiftDialog]);

  const { locales } = useBrowserLocale({});

  const onOpenAddressDialog = useCallback(
    (from: string) => {
      setAddressDialogTrigger(from);
      openAddressDialog();
    },
    [openAddressDialog, setAddressDialogTrigger],
  );

  const checkIsConsideredOutsideOfRange = useEventCallback(() => {
    const hasShippingAddress = !!registryShippingAddress?.countryCode;
    return hasShippingAddress ? registryShippingAddress.countryCode !== 'USA' : locales[0] !== LOCALE;
  }, [registryShippingAddress]);

  const showOutOfUSAWarningOrAddItem = useCallback(
    (cb: () => void) => {
      if (checkIsConsideredOutsideOfRange()) {
        openCurrencyWarningDialog();
      } else {
        cb();
      }
    },
    [checkIsConsideredOutsideOfRange, openCurrencyWarningDialog],
  );

  const wrappedShowOutOfUSAWarningOrAddItem = useCallback(() => {
    showOutOfUSAWarningOrAddItem(executeDeferredAddCatalogItemCb);
  }, [showOutOfUSAWarningOrAddItem, executeDeferredAddCatalogItemCb]);

  const handleAddCatalogItemWithoutAddress = useCallback(() => {
    executeDeferredAddCatalogItemCb();
    clearDeferredAddCatalogItemCb();
  }, [clearDeferredAddCatalogItemCb, executeDeferredAddCatalogItemCb]);

  const verifyAddressAndTryAddCatalogItem = useCallback(
    (addCatalogItemCb: () => void) => {
      // Store add catalog item callback to be called when appropriate
      deferAddCatalogItemCb(addCatalogItemCb);
      if (registryShippingAddress) {
        // Must verify shipping address before adding catalog item
        wrappedShowOutOfUSAWarningOrAddItem();
      } else if (cookedProducts.counts.deliverable >= GIFT_COUNT_OFFSET) {
        // Prompt user to add a shipping address.
        openAddressReminderDialog();
      } else {
        handleAddCatalogItemWithoutAddress();
      }
    },
    [deferAddCatalogItemCb, wrappedShowOutOfUSAWarningOrAddItem, handleAddCatalogItemWithoutAddress, openAddressReminderDialog, registryShippingAddress, cookedProducts],
  );

  const ctx = useMemo<GeneralDialogsDriverContext>(() => {
    return {
      closeAddressDialog,
      closeAddressReminderDialog,
      closeCurrencyDialog,
      isAddressDialogOpen,
      isAddressReminderDialogOpen,
      isCurrencyDialogOpen,
      openAddressReminderDialog,
      openAddressDialog: onOpenAddressDialog,
      openCurrencyDialog,
      showOutOfUSAWarningOrAddItem,
      registryShippingAddress,
      verifyAddressAndTryAddCatalogItem,
    };
  }, [
    closeAddressReminderDialog,
    closeAddressDialog,
    closeCurrencyDialog,
    isAddressDialogOpen,
    isAddressReminderDialogOpen,
    isCurrencyDialogOpen,
    openAddressReminderDialog,
    onOpenAddressDialog,
    openCurrencyDialog,
    registryShippingAddress,
    showOutOfUSAWarningOrAddItem,
    verifyAddressAndTryAddCatalogItem,
  ]);

  //-----------------------------------
  // Address Reminder Dialog callbacks
  const handleOnAddressReminderActionClick = useCallback(() => {
    closeAddressReminderDialog();
    onOpenAddressDialog('AddressReminderDialog');
  }, [closeAddressReminderDialog, onOpenAddressDialog]);

  /**
   * Closing the AddressReminderDialog directly will clear out any deferred
   * add catalog item callback.
   */
  const handleOnCloseAddressReminderDialog = useCallback(() => {
    executeDeferredAddCatalogItemCb();
    clearDeferredAddCatalogItemCb();
    closeAddressReminderDialog();
  }, [closeAddressReminderDialog, clearDeferredAddCatalogItemCb, executeDeferredAddCatalogItemCb]);

  //-----------------------------------
  // Address Dialog callbacks
  const handleOnCloseAddressDialog = useCallback(() => {
    executeDeferredAddCatalogItemCb();
    clearDeferredAddCatalogItemCb();
    closeAddressDialog();
  }, [clearDeferredAddCatalogItemCb, closeAddressDialog, executeDeferredAddCatalogItemCb]);

  const handleOnSaveAddress = useCallback(
    (shippingAddress: ShippingAddressFragment) => {
      closeAddressDialog();
      if (checkHasDeferredAddCatalogItemCb()) {
        wrappedShowOutOfUSAWarningOrAddItem();
      }
    },
    [closeAddressDialog, wrappedShowOutOfUSAWarningOrAddItem, checkHasDeferredAddCatalogItemCb],
  );

  const { onClick: showInstallBrowserButton } = useInstallBrowserButtonProps({ eventId, website });

  return (
    <Provider value={ctx}>
      <ShippingAddressTelemetryProvider>
        {children}
        <CurrencyDialog
          isOpen={isCurrencyDialogOpen}
          onClose={closeCurrencyDialog}
          eventId={eventId}
          registryCurrency={registryCurrency}
          setRegistryCurrency={onSetRegistryCurrency}
        />
        <ShopCurrencyWarningDialog
          isOpen={isCurrencyWarningDialogOpen}
          onClose={closeCurrencyWarningDialog}
          eventId={eventId}
          onAddAnyway={executeDeferredAddCatalogItemCb}
          onAddGift={handleOnAddCustomGiftClick}
          showInstallBrowserButton={showInstallBrowserButton}
        />
        <AddressReminderDialog
          isOpen={isAddressReminderDialogOpen}
          eventId={eventId}
          onClose={handleOnCloseAddressReminderDialog}
          onActionClick={handleOnAddressReminderActionClick}
          orders={orders}
          finalizedEventDate={finalizedEventDate}
          registries={registries}
        />
        <AddressDialog
          eventId={eventId}
          registryShippingAddress={registryShippingAddress}
          isOpen={isAddressDialogOpen}
          onSave={handleOnSaveAddress}
          onClose={handleOnCloseAddressDialog}
          addressDialogTrigger={addressDialogTrigger}
          size={addressDialogTrigger === 'TransferRegistryDialog' ? 'xl' : '2xl'}
        />
      </ShippingAddressTelemetryProvider>
    </Provider>
  );
};

export {
  /**
   * Grants access to these dialogs:
   * - `<CurrencyDialog />`
   * - `<CurrencyWarningDialog />`
   * - `<AddressReminderDialog />`
   * - `<AddressDialog />`
   *
   * Also exposes utility method to defer adding a catalog item until the
   * shipping address is validated or user elects to add anyways.
   * - `verifyAddressAndTryAddCatalogItem()`
   */
  useShippingAddressAndCurrencyDialogsContext,
};
